<template>
  <v-container fluid>
    <!-- Breadcrumbs -->
    <v-row class="align-center pl-3">
      <v-breadcrumbs :items="breadcrumbs" divider="/"></v-breadcrumbs>
      <v-spacer />
    </v-row>

    <v-row justify="end">
      <v-card-title class="pb-0">
        <v-btn
          v-if="me && me.isSuperuser"
          color="primary"
          dark
          outlined
          rounded
          small
          @click="openCreate">
          {{ $t("announcements.actions.new") }}
        </v-btn>
      </v-card-title>
    </v-row>

    <!-- Centered page content -->
    <v-row class="mt-6" justify="center">
      <v-col cols="12" md="8" lg="7">
        <h1 class="text-center font-weight-bold mb-6">
          {{$t("announcements.announcementTitle")}}
        </h1>

        <v-dialog v-model="createDialog" max-width="900" :retain-focus="false">
          <v-card>
            <v-card-title>{{$t('announcements.dialogs.createTitle')}}</v-card-title>
            <v-divider />
            <v-card-text>
              <v-text-field v-model="form.title" :label="$t('announcements.fields.title')" />
              <v-text-field v-model="form.teaser" :label="$t('announcements.fields.teaser')" />
              <v-text-field v-model="form.publishDate" :label="$t('announcements.fields.publishAt')" type="date" />
              <v-switch v-model="form.isPublished" :label="$t('announcements.fields.isPublished')" />

              <tiptap-vuetify
                v-model="form.content"
                :extensions="extensions"
                :editor-properties="{ editable: true }"
              />
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="createDialog=false">{{$t('announcements.common.cancel')}}</v-btn>
              <v-btn color="primary" @click="createAnnouncement" :loading="isSaving" :disabled="isSaving">{{$t('announcements.common.save')}}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-expansion-panels multiple accordion v-model="openPanels">
          <v-expansion-panel v-for="a in announcementsSorted" :key="a.id">
            <v-expansion-panel-header>
              <template v-slot:default="{ open }">
                <div class="d-flex align-center" style="width: 100%;">
                  <!-- LINKS: titel + teaser (alleen wanneer dicht) -->
                  <div class="min-w-0">
                    <div class="font-weight-bold">
                      {{ a.title }}
                    </div>

                    <div
                      v-if="!open && a.teaser"
                      class="grey--text text--darken-1 text-caption mt-1 teaser-line"
                    >
                      {{ a.teaser }}
                    </div>
                  </div>

                  <v-spacer />

                  <!-- RECHTS: datum + bolletje + edit/delete -->
                  <div class="d-flex align-center">
                    <div class="grey--text text--darken-1 mr-2">
                      {{ formatDate(a.publishAt) }}
                    </div>

                    <v-icon
                      small
                      class="mr-2"
                      :color="isRead(a.id) ? 'success' : 'warning'"
                    >
                      fiber_manual_record
                    </v-icon>

                    <template v-if="me.isSuperuser">
                      <v-btn icon small @click.stop="openEdit(a)">
                        <v-icon small>edit</v-icon>
                      </v-btn>

                      <v-btn icon small color="error" @click.stop="openDelete(a)">
                        <v-icon small>delete</v-icon>
                      </v-btn>
                    </template>
                  </div>
                </div>
              </template>
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <div class="py-4">
                <p v-if="a.teaser" class="text-body-1 mb-4">
                  {{ a.teaser }}
                </p>

                <tiptap-vuetify
                  v-if="a.content"
                  v-model="tiptapContentById[a.id]"
                  :extensions="extensions"
                  :editor-properties="{ editable: false }"
                >
                  <template #toolbar=""><pre></pre></template>
                </tiptap-vuetify>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-dialog v-model="editDialog" max-width="900" :retain-focus="false">
          <v-card>
            <v-card-title>{{$t('announcements.dialogs.editTitle')}}</v-card-title>
            <v-divider />
            <v-card-text>
              <v-text-field v-model="form.title" :label="$t('announcements.fields.title')" />
              <v-text-field v-model="form.teaser" :label="$t('announcements.fields.teaser')" />
              <v-text-field v-model="form.publishDate" :label="$t('announcements.fields.publishAt')" type="date"/>
              <v-switch v-model="form.isPublished" :label="$t('announcements.fields.isPublished')" />

              <tiptap-vuetify
                v-model="form.content"
                :extensions="extensions"
                :editor-properties="{ editable: true }"
              />
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="editDialog=false">{{$t('announcements.common.cancel')}}</v-btn>
              <v-btn color="primary" @click="updateAnnouncement" :loading="isSaving" :disabled="isSaving">{{$t('announcements.common.save')}}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="deleteDialog" max-width="520" :retain-focus="false">
          <v-card>
            <v-card-title>{{$t('announcements.dialogs.deleteTitle')}}</v-card-title>
            <v-card-text>
               {{ $t("announcements.dialogs.deleteConfirm", { title: selectedAnnouncement?.title }) }}
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="deleteDialog=false">{{$t('announcements.common.cancel')}}</v-btn>
              <v-btn color="error" @click="deleteAnnouncementConfirmed">{{$t('announcements.common.delete')}}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import gql from "graphql-tag";
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
  Image,
} from "tiptap-vuetify";

const MARK_READ = gql`
  mutation markAnnouncementAsRead($input: MarkAnnouncementAsReadInput!) {
    markAnnouncementAsRead(input: $input) {
      announcementRead {
        id
        announcement { id }
      }
    }
  }`;

const CREATE_ANNOUNCEMENT = gql`
  mutation createAnnouncement($input: CreateAnnouncementInput!) {
    createAnnouncement(input: $input) {
      announcement {
        id
        title
        teaser
        content
        publishAt
        isPublished
        isPinned
      }
    }
  }`;
const UPDATE_ANNOUNCEMENT = gql`mutation updateAnnouncement($input: UpdateAnnouncementInput!) {
  updateAnnouncement(input: $input) {
    announcement {
      id
      title
      teaser
      content
      publishAt
      isPublished
      isPinned
    }
  }
}`;
const DELETE_ANNOUNCEMENT = gql`
  mutation deleteAnnouncement($input: DeleteAnnouncementInput!) {
    deleteAnnouncement(input: $input) {
      announcement { id }
    }
  }`;

export default {
  name: "announcements",

  components: {
    TiptapVuetify,
  },

  apollo: {
    announcementsConnection: {
      query: gql`
        query announcements($search: String) {
          announcements(first: 100, search: $search) {
            edges {
              node {
                id
                title
                teaser
                content
                publishAt
                isPinned
                isPublished
                created
                modified
              }
            }
          }
        }
      `,
      variables() {
        return { search: null };
      },
      fetchPolicy: "cache-and-network",
      update: (data) => data.announcements,
      skip() {
        return !this.$store.state.user.loggedIn;
      },
    },

    announcementReadsConnection: {
      query: gql`
        query announcementReads {
          announcementReads(first: 100) {
            edges {
              node {
                announcement {
                  id
                }
              }
            }
          }
        }
      `,
      fetchPolicy: "cache-and-network",
      update: (data) => data.announcementReads,
      skip() {
        return !this.$store.state.user.loggedIn;
      },
    },
  },

  data() {
    return {
      openPanels: [],

      announcementsConnection: null,
      announcementReadsConnection: null,

      // Per announcement ID de TipTap content (HTML string)
      tiptapContentById: {},

      // dialogs
      createDialog: false,
      editDialog: false,
      deleteDialog: false,

      // selected / form model
      selectedAnnouncement: null,
      form: {
        id: null,
        title: "",
        teaser: "",
        content: "",
        publishAt: null,
        isPublished: false,
        isPinned: false,
      },

      extensions: [
        History,
        Blockquote,
        Link,
        Underline,
        Strike,
        Italic,
        ListItem,
        BulletList,
        OrderedList,
        [
          Heading,
          {
            options: {
              levels: [1, 2, 3],
            },
          },
        ],
        Bold,
        Code,
        HorizontalRule,
        Paragraph,
        HardBreak,
        Image,
      ],

      isSaving: false,
    };
  },

  computed: {
    me() {
      return this.$store.state.user.me || {};
    },

    breadcrumbs() {
      return [
        { text: this.$t("announcements.breadcrumbs.home"), disabled: false, href: "/#/" },
        { text: this.$t("announcements.breadcrumbs.announcements"), disabled: true },
      ];
    },

    announcements() {
      const edges = this.announcementsConnection?.edges || [];
      return edges.map((e) => e.node);
    },

    announcementsSorted() {
      // pinned eerst, daarna publishAt desc
      return [...this.announcements].sort((a, b) => {
        const ap = a.isPinned ? 1 : 0;
        const bp = b.isPinned ? 1 : 0;
        if (ap !== bp) return bp - ap;
        return (a.publishAt < b.publishAt) ? 1 : -1;
      });
    },

    readIdSet() {
      const edges = this.announcementReadsConnection?.edges || [];
      return new Set(edges.map((e) => e.node.announcement.id).filter(Boolean));
    },
  },

  watch: {
    // Zet TipTap content zodra announcements geladen zijn
    announcements: {
      immediate: true,
      handler(list) {
        const map = { ...this.tiptapContentById };
        list.forEach((a) => {
          if (map[a.id] == null) map[a.id] = a.content || "";
        });
        this.tiptapContentById = map;
      },
    },

    openPanels(val) {
      const open = Array.isArray(val) ? val : [val];

      open.forEach((panelIndex) => {
        const a = this.announcementsSorted[panelIndex];
        if (!a) return;

        if (!this.isRead(a.id)) {
          this.markAnnouncementAsRead(a.id);
        }
      });
    },
  },

  methods: {
    isRead(announcementNodeId) {
      return this.readIdSet.has(announcementNodeId);
    },

    formatDate(iso) {
      // simpele YYYY-MM-DD weergave (pas aan naar wens)
      if (!iso) return "";
      return String(iso).slice(0, 10);
    },

    markAnnouncementAsRead(announcementNodeId) {
      return this.$apollo
        .mutate({
          mutation: MARK_READ,
          variables: { input: { announcementId: announcementNodeId } },
        })
        .then(() => {
          this.$apollo.queries.announcementReadsConnection.refresh();
        })
        .catch(console.log);
    },

    // ----- UI openers -----
    openCreate() {
      const today = new Date().toISOString().slice(0, 10);

      this.form = {
        id: null,
        title: "",
        teaser: "",
        content: "",
        publishDate: today,
        isPublished: false,
        isPinned: false,
      };
      this.createDialog = true;
    },

    openEdit(a) {
      this.form = {
        id: a.id,
        title: a.title || "",
        teaser: a.teaser || "",
        content: this.tiptapContentById[a.id] ?? a.content ?? "",
        publishDate: this.toDateInput(a.publishAt),
        isPublished: a.isPublished ?? false,
        isPinned: a.isPinned ?? false,
      };
      this.editDialog = true;
    },

    openDelete(a) {
      this.selectedAnnouncement = a;
      this.deleteDialog = true;
    },

    // ----- CRUD actions -----
    async createAnnouncement() {
      if (this.isSaving) return;
      this.isSaving = true;

      try {
        await this.$apollo.mutate({
          mutation: CREATE_ANNOUNCEMENT,
          variables: {
            input: {
              title: this.form.title,
              teaser: this.form.teaser || null,
              content: this.form.content,
              publishAt: this.toIsoFromDateInput(this.form.publishDate),
              isPublished: this.form.isPublished,
              isPinned: this.form.isPinned,
            },
          },
        });

        this.createDialog = false;
        await this.$apollo.queries.announcementsConnection.refresh();
      } catch (e) {
        console.log(e);
      } finally {
        this.isSaving = false;
      }
    },

    async updateAnnouncement() {
      if (this.isSaving) return;
      this.isSaving = true;

      try {
        const res = await this.$apollo.mutate({
          mutation: UPDATE_ANNOUNCEMENT,
          variables: {
            input: {
              id: this.form.id,
              title: this.form.title,
              teaser: this.form.teaser || null,
              content: this.form.content,
              publishAt: this.toIsoFromDateInput(this.form.publishDate),
              isPublished: this.form.isPublished,
              isPinned: this.form.isPinned,
            },
          },
          refetchPolicy: "no-cache",
        });

        const updated = res?.data?.updateAnnouncement?.announcement;

        if (updated?.id) {
          this.$set(this.tiptapContentById, updated.id, updated.content || "");
        }

        this.editDialog = false;

        await this.$apollo.queries.announcementsConnection.refetch?.() ||
          this.$apollo.queries.announcementsConnection.refresh();
      } catch (e) {
        console.log(e);
      } finally {
        this.isSaving = false;
      }
    },

    async deleteAnnouncementConfirmed() {
      if (!this.selectedAnnouncement?.id) return;

      await this.$apollo.mutate({
        mutation: DELETE_ANNOUNCEMENT,
        variables: { input: { id: this.selectedAnnouncement.id } },
      });

      this.deleteDialog = false;
      this.selectedAnnouncement = null;
      this.$apollo.queries.announcementsConnection.refresh();
    },

    toDateInput(iso) {
      if (!iso) return null;
      return String(iso).slice(0, 10);
    },

    toIsoFromDateInput(dateStr) {
      if (!dateStr) return null;
      return new Date(dateStr + "T00:00:00.000Z").toISOString();
    },
  },
};
</script>

<style scoped>
/* optioneel: TipTap content iets netter */
::v-deep .ProseMirror {
  padding: 0;
}
</style>
